import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import {
  AutoPolicy,
  GracePeriodDays,
  OldReplaceVehicleQuoteApiRequest,
  Policy,
  PolicyAnalytics
} from '@amfam/policy/models';
import { fromVehicleActions, vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, ErrorMsg } from '@amfam/shared/utility/shared-services';
import { ErrorLevels, ErrorObj, LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';

@Component({
  selector: 'ds-year-make-model-lookup',
  templateUrl: './year-make-model-lookup.component.html',
  styleUrls: ['./year-make-model-lookup.component.scss'],
  standalone: false
})
export class YearMakeModelLookupComponent implements OnInit, OnDestroy {
  @Input() policyNumber: string;
  @Input() replaceVin: string;

  hasError: Observable<boolean>;
  errorMessage: string;
  errorList: Array<ErrorObj> = [];

  // Grace period
  displayPolicyGracePeriod = false;
  gracePeriodDays$: Observable<string>;
  private policyList: Policy[];
  policy: AutoPolicy;

  // Select box arrays
  years = [];
  makes = [];
  models = [];
  styles = [];
  private isAuthorized = true;

  // Form elements
  yearMakeModelForm: UntypedFormGroup;
  yearCtrl: AbstractControl;
  makeCtrl: AbstractControl;
  modelCtrl: AbstractControl;
  styleCtrl: AbstractControl;

  // Keeps track of whether an API call is in-flight
  loading: Observable<boolean>;

  // Keeps track of which form element was just selected / is loading
  currentLoad: string;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  public activeAgents$ = combineLatest(
    this.store.select(fromRoot.getAllActiveAgents),
    this.store.select(agentQuery.getAreAllAgentsLoaded),
    (agents, loaded) => ({ agents, loaded })
  ).pipe(
    filter(data => !!data.loaded),
    map(data => data.agents)
  );

  public getAdvanceAutoPolicies$ = this.store.select(PolicySelectors.selectAdvanceAutoPolicies);
  public userState$ = this.store.select(userQuery.selectUserState);

  constructor(
    private store: Store<fromRoot.RootState>,
    private formBuilder: UntypedFormBuilder,
    private analyticsFacade: AnalyticsFacade,
    public roleService: ImpersonateRolesService,
    public spinner: LoadingSpinnerService,
    public copyService: CopyService
  ) {}

  ngOnInit() {
    // Loading indicator
    this.loading = combineLatest(
      this.store.select(vehicleQuery.selectLoading),
      this.store.select(fromRoot.getReplaceVehicleLoading),
      (vehicleSelectLoading, replaceVehicleLoading) => {
        return vehicleSelectLoading || replaceVehicleLoading;
      }
    );

    this.hasError = this.store.select(vehicleQuery.selectHasError);

    combineLatest(
      this.store.select(vehicleQuery.selectHasError),
      this.store.select(vehicleQuery.selectLoading),
      this.store.select(vehicleQuery.selectStatus),
      (error, loading, status) => {
        return {
          loading,
          error,
          status
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => !state.loading)
      )
      .subscribe(state => {
        this.spinner.stop();
        if (state.error) {
          // Check the state if it has an error before stopping the spinner and displaying a message.
          this.errorMessage = this.copyService.get('policy', 'defaultReplaceVehicleErrorMessage');
          if (state.status) {
            this.copyService.get('policy', 'errorMsgs').some((errMsgObj: ErrorMsg) => {
              if (errMsgObj.key === _get(state, 'status.errors[0].message', '')) {
                this.errorMessage = errMsgObj.value;
                return true;
              }
            });
          }
        }
      });

    combineLatest(
      this.store.select(vehicleQuery.selectYears),
      this.store.select(vehicleQuery.selectMakes),
      this.store.select(vehicleQuery.selectModels),
      this.store.select(vehicleQuery.selectStyles),
      (years, makes, models, styles) => {
        return {
          years,
          makes,
          models,
          styles
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.years = _get(state, 'years', []);
        this.makes = _get(state, 'makes', []);
        this.models = _get(state, 'models', []);
        this.styles = _get(state, 'styles', []);

        if (this.styleCtrl) {
          this.styleCtrl.setValidators(null);
          if (this.styles.length > 0) {
            this.styleCtrl.setValidators(ValidationService.unsetStringValidator);
          }
          this.styleCtrl.updateValueAndValidity();
        }
      });

    combineLatest(
      this.store.select(fromRoot.getReplaceVehicleHasError),
      this.store.select(fromRoot.getReplaceVehicleStatus),
      this.roleService.isAuthorized('replacevehicle_yearmakemodel'),
      (hasError, status, isAuthorized) => {
        return {
          hasError,
          status,
          isAuthorized
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.hasError) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/risk-selection/grace-period']
            })
          );
          this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyReplaceVehicleError);
        }
        this.isAuthorized = state.isAuthorized;
      });

    this.gracePeriodDays$ = combineLatest([
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(userQuery.selectUserStateCode)
    ]).pipe(
      takeUntil(this.stop$),
      map((advancedAutoPolicies, userState) => {
        const advancedAutoPoliciesLength = _get(advancedAutoPolicies, 'length', 0);
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        return advancedAutoPoliciesLength > 0 || !isExtendedGracePeriodState
          ? GracePeriodDays.REGULAR // 14
          : GracePeriodDays.EXTENDED; // 30
      })
    );

    this.buildForm();
    this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyReplaceVehicleYMM);
    if (!this.isAuthorized) {
      this.yearMakeModelForm.disable();
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }

  submit() {
    this.spinner.start({
      secondaryMessage: this.copyService.getCopy('policy.quoteCalculatingLoadingSpinnerText'),
      blockActions: true
    });

    if (
      _get(this.yearCtrl, 'value', 'unset') !== 'unset' &&
      _get(this.makeCtrl, 'value', 'unset') !== 'unset' &&
      _get(this.modelCtrl, 'value', 'unset') !== 'unset'
    ) {
      // This component is only used in the old flow, so we don't need any feature toggling
      const payload: OldReplaceVehicleQuoteApiRequest = {
        policyNumber: this.policyNumber,
        effectiveDate: new Date(),
        replaceVIN: this.replaceVin,
        newVehicle: {
          make: this.makeCtrl.value,
          model: this.modelCtrl.value,
          vehicleType: 'auto',
          year: this.yearCtrl.value,
          series: _get(this.styleCtrl, 'value')
        }
      };
      if (_get(this.styleCtrl, 'value', 'unset') !== 'unset') {
        payload.newVehicle.series = this.styleCtrl.value;
      }
      this.store.dispatch(new replaceVehicleActions.ReplaceVehicleGetPremiumAction(payload));
    }
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/replace-vehicle']
      })
    );
  }

  private buildForm() {
    this.yearMakeModelForm = this.formBuilder.group({
      year: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
      make: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
      model: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
      style: ['unset']
    });

    this.yearCtrl = this.yearMakeModelForm.controls['year'];
    this.makeCtrl = this.yearMakeModelForm.controls['make'];
    this.modelCtrl = this.yearMakeModelForm.controls['model'];
    this.styleCtrl = this.yearMakeModelForm.controls['style'];

    this.yearCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(data => {
        this.yearChanged();
      });

    this.makeCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(data => {
        this.makeChanged();
      });

    this.modelCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(data => {
        this.modelChanged();
      });

    this.styleCtrl.valueChanges
      .pipe(takeUntil(this.stop$), distinctUntilChanged())
      .subscribe(data => {
        this.styleChanged();
      });

    this.errorList = [];
  }

  private yearChanged() {
    if (_get(this.yearCtrl, 'value') !== 'unset') {
      this.currentLoad = 'year';
      const payload = {
        year: this.yearCtrl.value
      };
      this.makeCtrl.reset('unset');
      this.modelCtrl.reset('unset');
      this.styleCtrl.reset('unset');
      this.store.dispatch(fromVehicleActions.GetMakes(payload));
    }
  }

  private makeChanged() {
    if (_get(this.makeCtrl, 'value') !== 'unset') {
      this.currentLoad = 'make';
      const payload = {
        year: this.yearCtrl.value,
        make: this.makeCtrl.value
      };
      this.modelCtrl.reset('unset');
      this.styleCtrl.reset('unset');
      this.store.dispatch(fromVehicleActions.GetModels(payload));
    }
  }

  private modelChanged() {
    if (_get(this.modelCtrl, 'value') !== 'unset') {
      this.currentLoad = 'model';
      const payload = {
        year: this.yearCtrl.value,
        make: this.makeCtrl.value,
        model: this.modelCtrl.value
      };
      this.styleCtrl.reset('unset');
      this.store.dispatch(fromVehicleActions.GetStyles(payload));
    }
  }

  private styleChanged() {
    if (_get(this.styleCtrl, 'value') !== 'unset') {
      this.currentLoad = 'style';
      const payload = {
        year: this.yearCtrl.value,
        make: this.makeCtrl.value,
        model: this.modelCtrl.value,
        style: this.styleCtrl.value
      };
    }
  }

  private showErrorMessage(errorMessageList: Array<String>, errorLevel?: ErrorLevels) {
    this.errorList = [];
    if (errorMessageList) {
      errorMessageList.forEach((msg: string) => {
        if (!this.errorList.find(error => error.errorMessage === msg)) {
          if (msg === 'Unable to quote with supplied information.') {
            msg =
              'Unfortunately, we cannot provide a quote for this vehicle at this time. Please contact your agent for assistance.';
          }
          this.spinner.stop();
          this.errorList.push({ errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH });
        }
      });
    }
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = !this.displayPolicyGracePeriod;
  }
}
